// Paragraph.tsx
import parse, {
  domToReact,
  Element,
  HTMLReactParserOptions,
} from 'html-react-parser';
import React from 'react';

interface Props {
  content: string;
  className?: string;
}

const Paragraph = ({ content, className }: Props) => {
  const options: HTMLReactParserOptions = {
    replace: (node) => {
      // only handle <p> tags
      if ((node as Element).type === 'tag' && (node as Element).name === 'p') {
        const pNode = node as Element;
        const children = pNode.children ?? [];

        // Check if there is meaningful text outside anchor(s) OR non-anchor elements
        const hasTextNode = children.some(
          (ch) =>
            ch.type === 'text' && String((ch as any).data).trim().length > 0,
        );
        const hasNonAnchorElement = children.some(
          (ch) => ch.type === 'tag' && (ch as Element).name !== 'a',
        );

        // If p has text nodes or non-anchor elements, we treat it as "text + anchor inside"
        if (hasTextNode || hasNonAnchorElement) {
          // Render children, but transform descendant <a> tags adding the helper class
          const newChildren = domToReact(children as any, {
            replace: (child) => {
              if (
                (child as Element).type === 'tag' &&
                (child as Element).name === 'a'
              ) {
                const aNode = child as Element;

                // preserve existing class attribute (attribs.class or attribs.className)
                const existingClass =
                  (aNode.attribs &&
                    (aNode.attribs.class || aNode.attribs.className)) ||
                  '';
                const combined = (existingClass + ' styled-link').trim();

                // Build React anchor element with preserved attributes and new className
                const { href, target, rel, style, ...rest } =
                  aNode.attribs || {};

                // Avoid TypeScript complaining about spreading unknown HTML attributes
                // into a JSX element by casting the remaining attributes to `any`.
                return (
                  <a
                    href={href}
                    target={target}
                    rel={rel}
                    {...(rest as any)}
                    className={combined}>
                    {domToReact(aNode.children as any)}
                  </a>
                );
              }
              return undefined;
            },
          });

          // Return React <p> element with the transformed children
          return <p>{newChildren}</p>;
        }

        // If p contains only anchor(s) (no text nodes or other elements),
        // render anchors with the `button-link` helper class instead.
        const onlyAnchorsChildren = domToReact(children as any, {
          replace: (child) => {
            if (
              (child as Element).type === 'tag' &&
              (child as Element).name === 'a'
            ) {
              const aNode = child as Element;

              const existingClass =
                (aNode.attribs &&
                  (aNode.attribs.class || aNode.attribs.className)) ||
                '';
              const combined = (existingClass + ' button-link').trim();

              const { href, target, rel, style, ...rest } = aNode.attribs || {};

              return (
                <a
                  href={href}
                  target={target}
                  rel={rel}
                  {...(rest as any)}
                  className={combined}>
                  {domToReact(aNode.children as any)}
                </a>
              );
            }
            return undefined;
          },
        });

        return <p>{onlyAnchorsChildren}</p>;
      }

      // default: don't replace
      return undefined;
    },
  };

  return <div className={className}>{parse(content, options)}</div>;
};

export default Paragraph;
